/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { PythonRuntime, PythonCell } from '../compiler/runtime';
import styles from './file.module.css' with { type: 'css' };
import html from './file.module.html';

// TODO 
// emit event when changed
// errors for invalid file upload

/**
 * ## TunePadFile cell writes a file to the python file system. Format will be inferred from file extension.
 * ```html
 * <tunepad-file
 *    uuid = "{{ database UUID }}"
 *    name = "{{ valid python file name path (e.g. "words.txt", "words.csv", "words.json" )}}">
 * contents of file
 * go in the inner html
 * </tunepad-file>
 * ```
 */
export class TunePadFile extends HTMLElement {

    static readonly ELEMENT = "tunepad-file";

    static observedAttributes = [ ];

    /** all of the HTML elements are contained within a shadow DOM */
    private root : ShadowRoot;

    /** full file path name */
    public get name() { return this.makePythonSafe(this.getAttribute('name') ?? 'new_file.txt') }

    private _loaded = false;



    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;

        // singleton object and it's safe to call init() multiple times
        PythonRuntime.init();
    }


    async connectedCallback() {
        await this.waitForWindowLoad();

        // write file to pyodide file system
        this.updateFile(this.innerHTML);
        this.innerHTML = '';

        // event listeners
        this.root.querySelector('.toolbar')?.addEventListener('click', (evt) => {
            this.toggleClass('.container', 'expanded');
        });

        const input = this.root.querySelector('#file-input') as HTMLInputElement;
        input.addEventListener('change', e => {
            if (input.files && input.files.length > 0) this.replaceFile(input.files[0]);
        });

        this.root.querySelector('#upload-button')?.addEventListener('click', (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            input.click();
        });

        this.root.querySelector('#showing')?.addEventListener('click', async e => {
            const data = await PythonRuntime.readFile(this.name);
            this.setHTML('#contents', this.formatData(data));
            this.addClass('#showing', 'hidden');
        });

        this._loaded = true;
    }

    disconnectedCallback() { PythonRuntime.deleteFile(this.name); }
    attributeChangedCallback(name : string, oldValue : string, newValue : string) { }



    /**
     * User can upload a new file to replace the existing one
     */
    public replaceFile(file : File) {
        if (this.validFileType(file) && file.size <= 1e6) {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.addEventListener("load", () => {
                const oldPath = this.name;
                this.setAttribute('name', file.name);
                PythonRuntime.moveFile(oldPath, this.name);
                this.updateFile(reader.result as string);

                this.dispatchEvent(new CustomEvent("update-file", {
                    bubbles: false,
                    composed: true,
                    detail: { name : this.name }
                }));
            });
        }
    }


    /**
     * Return a serialized snapshot of state
     */
    public async save() : Promise<any> {
        const data : any = { };
        data.uuid = this.getAttribute('uuid');
        data.name = this.getAttribute('name');
        data.data = await PythonRuntime.readFile(this.name);
        return data;
    }

    /**
     * Restore from a saved version
     */
    public restore(data : any) {
        this.setAttribute('name', data.name);
        if (!this._loaded) {
            this.innerHTML = data.data;
        } else {
            this.updateFile(data.data);
        }
    }


    /**
     * Update file information from file contents...
     */
    private updateFile(data : string) {
        const formatted = this.formatData(data);
        this.setHTML('#contents', this.subdata(formatted, 2000));
        this.setHTML('#file-path', this.name);
        this.setHTML('#size', `${this.formatBytes(data.length)}, ${this.lineCount(formatted).toLocaleString()} lines`);
        this.root.querySelector('tunepad-icon')?.setAttribute('icon', `file-${this.fileType}`);

        if (formatted.length > 2000) {
            this.setHTML('#showing', `Showing 2,000 characters (click to show all ${this.formatBytes(data.length)})`);
            this.removeClass('#showing', 'hidden');
        } else {
            this.addClass('#showing', 'hidden');
        }
        PythonRuntime.writeFile(this.name, data);
    }
    

    /**
     * file format based on file extension
     */
    private get fileType() : string {
        const p = this.name.toLowerCase();
        if (p.endsWith('.txt') || p.endsWith('.text')) {
            return 'text';
        } else if (p.endsWith('.json')) {
            return 'json';
        } else if (p.endsWith('.csv')) {
            return 'csv';
        } else {
            return 'text';
        }
    }

    private validFileType(file : File) {
        return [
            'text/plain',
            'application/json',
            'text/csv'
        ].includes(file.type);
    }



    /**
     * format data depending on the file type
     */
    private formatData(data : string) : string {
        if (this.fileType === 'json') {
            try {
                return JSON.stringify(JSON.parse(data), null, 4);
            } catch (x) { ; }
        }
        return data;
    }

    /**
     * file size as string
     */
    private formatBytes(bytes : number) : string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
    }


    /**
     * Only display n characters of the file
     */
    private subdata(data : string, limit : number) : string {
        return (data.length > limit) ? data.substring(0, limit) : data;
    }


    /** 
     * Convert a string into a legal python file path name
     */
    private makePythonSafe(name: string) {
        let result = '';
        for (const char of name) {
            let c = char.charCodeAt(0);
            if ((c >= 48 && c <= 57) ||    // 0-9
                (c >= 97 && c <= 199) ||   // a-z
                (c >= 65 && c <= 90) ||    // A-Z
                [ 46, 47, 95 ].includes(c))   // '.' or '_' or '/'
            {
                result += char;
            }
            else if (c === 32) { // SPACE
                result += '_';
            }
            else if (c === 45) { // dash
                result += '_';
            }
        }

        if (result === '') {
            result = 'new_file';
        }

        // python identifiers can't start with a number
        if (result.charCodeAt(0) >= 48 && result.charCodeAt(0) <= 57) {
            result = '_' + result;
        }
        return result;
    }


    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') {
                resolve();
            } else {
                window.addEventListener('load', () => {
                    resolve();
                });
            }
        });
    }

    private lineCount(s : string) : number {
        let result = 0;
        for (let i=0; i<s.length; i++) if (s[i] === '\n') result++;
        return result;
    }

    private setHTML(selector : string, html : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = html;
    }

    private addClass(selector : string, className : string) {
        this.root.querySelector(selector)?.classList.add(className);
    }

    private removeClass(selector : string, className : string) {
        this.root.querySelector(selector)?.classList.remove(className);
    }

    private toggleClass(selector : string, className : string) {
        this.root.querySelector(selector)?.classList.toggle(className)
    }
}
